import { buildPerformanceTimeline, getNetTradeProfit, getPositionChronologyTime } from "./tradeStats";

const formatMonthKey = (dateValue) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const formatMonthLabel = (monthKey) => {
  if (!monthKey) {
    return "";
  }

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  return date.toLocaleString("en-GB", { month: "short", year: "2-digit" });
};

export const getTradeBreakdowns = (positions = [], startingAmount = 0, cashflows = []) => {
  if (!positions.length) {
    return {
      monthlyLabels: [],
      monthlyProfitValues: [],
      monthlyDrawdownValues: [],
      strategyLabels: [],
      strategyProfitValues: [],
      strategyWinRatioValues: [],
      symbolLabels: [],
      symbolProfitValues: [],
      symbolWinRatioValues: [],
      pairVolumeLabels: [],
      pairVolumeValues: [],
      pairVolumeTradeCounts: [],
      pairTradeLabels: [],
      pairTradeCountValues: [],
      pairTradeVolumeValues: [],
      totalTrades: 0,
    };
  }

  const sortedPositions = [...positions].sort((a, b) => {
    const aTime = new Date(getPositionChronologyTime(a)).getTime();
    const bTime = new Date(getPositionChronologyTime(b)).getTime();
    return aTime - bTime;
  });

  const monthlyMap = new Map();
  const strategyMap = new Map();
  const symbolMap = new Map();
  sortedPositions.forEach((position) => {
    const timeValue = getPositionChronologyTime(position);
    const monthKey = formatMonthKey(timeValue);
    const profit = getNetTradeProfit(position);
    const volume = Number(position.volume) || 0;
    const strategy = position.magic || "Unknown";
    const symbol = position.symbol || "Unknown";
    const isWin = profit > 0;
    const isLoss = profit < 0;

    if (!monthlyMap.has(monthKey)) {
      monthlyMap.set(monthKey, {
        label: formatMonthLabel(monthKey),
        profit: 0,
        maxDrawdown: 0,
      });
    }

    const monthStats = monthlyMap.get(monthKey);
    monthStats.profit += profit;

    if (!strategyMap.has(strategy)) {
      strategyMap.set(strategy, { profit: 0, wins: 0, losses: 0 });
    }

    if (!symbolMap.has(symbol)) {
      symbolMap.set(symbol, { profit: 0, wins: 0, losses: 0, volume: 0, trades: 0 });
    }

    const strategyStats = strategyMap.get(strategy);
    strategyStats.profit += profit;
    strategyStats.wins += isWin ? 1 : 0;
    strategyStats.losses += isLoss ? 1 : 0;

    const symbolStats = symbolMap.get(symbol);
    symbolStats.profit += profit;
    symbolStats.wins += isWin ? 1 : 0;
    symbolStats.losses += isLoss ? 1 : 0;
    symbolStats.volume += volume;
    symbolStats.trades += 1;

  });

  const timeline = buildPerformanceTimeline(sortedPositions, cashflows, startingAmount);
  timeline.points.forEach((point) => {
    if (point.eventType !== "trade") {
      return;
    }
    const monthStats = monthlyMap.get(formatMonthKey(point.time));
    if (monthStats) {
      monthStats.maxDrawdown = Math.max(monthStats.maxDrawdown, point.drawdown);
    }
  });

  const monthlyEntries = Array.from(monthlyMap.values());
  const strategyEntries = Array.from(strategyMap.entries())
    .sort((a, b) => b[1].profit - a[1].profit)
    .slice(0, 8);
  const symbolProfitEntries = Array.from(symbolMap.entries())
    .sort((a, b) => b[1].profit - a[1].profit)
    .slice(0, 8);
  const symbolVolumeEntries = Array.from(symbolMap.entries())
    .sort((a, b) => b[1].volume - a[1].volume || b[1].trades - a[1].trades)
    .slice(0, 8);
  const symbolTradeEntries = Array.from(symbolMap.entries())
    .sort((a, b) => b[1].trades - a[1].trades || b[1].volume - a[1].volume)
    .slice(0, 8);

  return {
    monthlyLabels: monthlyEntries.map((item) => item.label),
    monthlyProfitValues: monthlyEntries.map((item) => Number(item.profit.toFixed(2))),
    monthlyDrawdownValues: monthlyEntries.map((item) => Number(item.maxDrawdown.toFixed(2))),
    strategyLabels: strategyEntries.map(([label]) => label),
    strategyProfitValues: strategyEntries.map(([, value]) => Number(value.profit.toFixed(2))),
    strategyWinRatioValues: strategyEntries.map(([, value]) => {
      const decisiveTrades = value.wins + value.losses;
      const winRatio = decisiveTrades > 0 ? (value.wins / decisiveTrades) * 100 : 0;
      return Number(winRatio.toFixed(2));
    }),
    symbolLabels: symbolProfitEntries.map(([label]) => label),
    symbolProfitValues: symbolProfitEntries.map(([, value]) => Number(value.profit.toFixed(2))),
    symbolWinRatioValues: symbolProfitEntries.map(([, value]) => {
      const decisiveTrades = value.wins + value.losses;
      const winRatio = decisiveTrades > 0 ? (value.wins / decisiveTrades) * 100 : 0;
      return Number(winRatio.toFixed(2));
    }),
    pairVolumeLabels: symbolVolumeEntries.map(([label]) => label),
    pairVolumeValues: symbolVolumeEntries.map(([, value]) => Number(value.volume.toFixed(2))),
    pairVolumeTradeCounts: symbolVolumeEntries.map(([, value]) => value.trades),
    pairTradeLabels: symbolTradeEntries.map(([label]) => label),
    pairTradeCountValues: symbolTradeEntries.map(([, value]) => value.trades),
    pairTradeVolumeValues: symbolTradeEntries.map(([, value]) => Number(value.volume.toFixed(2))),
    totalTrades: sortedPositions.length,
  };
};
